/**
 * wireSettingsPanel — Settings overlay + __debugSettings (SPY-213 / SPY-413).
 */

import { createClientSettings } from '../../domain/settings/clientSettings.js';
import { createSettingsPanelController } from './settingsPanelController.js';
import {
  loadClientSettingsSnapshot,
  mergeClientSettingsSnapshot
} from '../settings/persistClientSettings.js';

function persistAll(storage, settings, remapsApi) {
  const snap = { ...settings.get() };
  if (remapsApi && typeof remapsApi.get === 'function') {
    snap.keybinds = remapsApi.get();
  }
  // Merge so volume/keybind saves keep ping + scoreboardSort (FID-HUD-01 / HUD-03).
  return mergeClientSettingsSnapshot(storage, snap);
}

/**
 * @param {{
 *   documentRef?: Document|{ getElementById?: Function },
 *   globalObj?: object|null,
 *   remapsApi?: { list?: Function, get?: Function, set?: Function },
 *   onVolumeChange?: (snap: object) => void,
 *   settings?: ReturnType<typeof createClientSettings>
 *   storage?: { getItem?: Function, setItem?: Function }|null
 * }} [opts]
 */
export function wireSettingsPanel({
  documentRef = globalThis.document,
  globalObj = typeof window !== 'undefined' ? window : null,
  remapsApi = null,
  onVolumeChange = null,
  settings = null,
  storage = typeof globalThis.localStorage !== 'undefined'
    ? globalThis.localStorage
    : null
} = {}) {
  const initial = loadClientSettingsSnapshot(storage) || {};
  const resolved = settings || createClientSettings(initial);
  const rootEl = documentRef?.getElementById?.('settings-overlay') || null;
  const masterVolumeEl =
    documentRef?.getElementById?.('settings-master-volume') || null;
  const sfxVolumeEl =
    documentRef?.getElementById?.('settings-sfx-volume') || null;
  const keybindsListEl =
    documentRef?.getElementById?.('settings-keybinds-list') || null;
  const openBtnEl = documentRef?.getElementById?.('settings-open-btn') || null;
  const closeBtnEl = documentRef?.getElementById?.('settings-close-btn') || null;

  if (
    initial.keybinds &&
    remapsApi &&
    typeof remapsApi.set === 'function'
  ) {
    remapsApi.set(initial.keybinds);
  }

  if (remapsApi && typeof remapsApi.set === 'function') {
    const innerSet = remapsApi.set.bind(remapsApi);
    remapsApi.set = (partial) => {
      const next = innerSet(partial);
      persistAll(storage, resolved, remapsApi);
      panel?.refresh?.();
      return next;
    };
  }

  if (remapsApi && typeof remapsApi.reset === 'function') {
    const innerReset = remapsApi.reset.bind(remapsApi);
    remapsApi.reset = () => {
      const next = innerReset();
      persistAll(storage, resolved, remapsApi);
      panel?.refresh?.();
      return next;
    };
  }

  let panel = null;

  const wrapped = {
    get: () => resolved.get(),
    getVolume: (ch) => resolved.getVolume(ch),
    setVolume(ch, value) {
      const next = resolved.setVolume(ch, value);
      persistAll(storage, resolved, remapsApi);
      if (typeof onVolumeChange === 'function') onVolumeChange(resolved.get());
      panel?.refresh?.();
      return next;
    }
  };

  panel = createSettingsPanelController({
    rootEl,
    masterVolumeEl,
    sfxVolumeEl,
    keybindsListEl,
    settings: wrapped,
    listKeybinds: () => (typeof remapsApi?.list === 'function'
      ? remapsApi.list()
      : [])
  });

  if (openBtnEl && typeof openBtnEl.addEventListener === 'function') {
    openBtnEl.addEventListener('click', () => panel.open());
  }
  if (closeBtnEl && typeof closeBtnEl.addEventListener === 'function') {
    closeBtnEl.addEventListener('click', () => panel.close());
  }

  if (globalObj) {
    globalObj.__debugSettings = {
      open: () => panel.open(),
      close: () => panel.close(),
      isOpen: () => panel.isOpen(),
      get: () => resolved.get(),
      setVolume: (ch, v) => wrapped.setVolume(ch, v),
      setActiveEntry: (id) => panel.setActiveEntry(id),
      getActiveEntry: () => panel.getActiveEntry(),
      listEntries: () => ['volume', 'keybinds'],
      listKeybinds: () => (typeof remapsApi?.list === 'function'
        ? remapsApi.list()
        : []),
      resetKeybinds: () => (typeof remapsApi?.reset === 'function'
        ? remapsApi.reset()
        : null),
      save: () => persistAll(storage, resolved, remapsApi),
      load: () => loadClientSettingsSnapshot(storage),
      storageKey: 'lol.clientSettings.v1'
    };
  }

  return {
    panel,
    settings: wrapped,
    debugApi: globalObj?.__debugSettings || null
  };
}
